import { Card, Grid, Select, Stack, Title } from "@mantine/core";
import {
  IConsequenceWithId,
  IEvaluationCategoryApi,
  IIssueBoardApi,
  IRisikoProbability,
} from "@schemas/issueBoardSchema";
import { IIssueApi, IIssueUpdateSchemaProps } from "@schemas/issueSchema";
import { useCallback, useMemo } from "react";
import { usePutProjectByProjectIdIssueUpdateSimpleMutation } from "../../../../../../../openapi";
import { McTitle } from "../../../../../../components";
import FoldableSection from "../../../../../../components/FoldableSection/FoldableSection";
import McTextAreaNew from "../../../../../../components/McInput/McTextAreaNew";
import IssueRisikoMatrix from "../../../../../../components/RisikoMatrix/IssueRisikoMatrix";
import LocalErrorBoundary from "../../../../../../util/LocalErrorBoundary";
import { createSimplePayload, guardedIssueUpdate } from "./modalUtils";

type IEvaluationCategoryWithId = IEvaluationCategoryApi & { id: string };

export const IssueItemRisikoSectionMantine: React.FC<{
  issue: IIssueApi;
  issueBoard: IIssueBoardApi;
  projectId: string;
}> = ({ issue, issueBoard, projectId }) => {
  const [updateValue] = usePutProjectByProjectIdIssueUpdateSimpleMutation();
  const buildSimplePayload = useMemo(() => createSimplePayload(projectId, issue._id), [projectId, issue._id]);
  const updateSimple = useCallback(
    async (prop: IIssueUpdateSchemaProps) => {
      await guardedIssueUpdate({
        issue,
        prop,
        mutation: updateValue,
        buildPayload: buildSimplePayload,
      });
    },
    [issue, updateValue, buildSimplePayload],
  );

  const probabilities: IRisikoProbability[] = Object.entries(issueBoard.risikoSection?.probabilites ?? {}).map(
    ([key, value]) => ({
      ...value,
      id: key,
    }),
  );

  const current: IRisikoProbability = probabilities.find((p: IRisikoProbability) => p.id === issue.risiko?.probability);

  const columns = useMemo(() => {
    if (issueBoard.risikoSection?.evaluationCategoriesConfig == null) {
      return [];
    }

    const _categories: [string, IEvaluationCategoryApi][] = Object.entries(
      issueBoard.risikoSection.evaluationCategoriesConfig,
    );

    if (_categories.length === 0) {
      return [];
    }

    const _cols = _categories.reduce((acc, [key, value]) => {
      if (value.isActive === false) {
        return acc;
      }

      acc.push({
        id: key,
        ...value,
      });

      return acc;
    }, [] as IEvaluationCategoryWithId[]);

    return _cols.map((row, rowIndex) => {
      const consequences: IConsequenceWithId[] = Object.entries(row.consequences)
        .map(([key, value]) => ({
          ...value,
          id: key,
        }))
        .filter((c) => c.isActive);

      const catId = row.id;
      const currentConsequence: string = issue.risiko?.consequence?.[catId] ?? null;

      const currVal: IConsequenceWithId = currentConsequence != null ? row.consequences[currentConsequence] : null;

      return (
        <Grid.Col key={rowIndex} span={4}>
          <McTitle title={row.name || "-"}>
            <Stack gap="sm">
              <Select
                allowDeselect={false}
                value={currVal?.id}
                data={consequences.map((c) => ({ label: c.name, value: c.id }))}
                onChange={(newValue) => {
                  updateSimple({ risiko: { consequence: { [catId]: newValue } } });
                  // onChange(newValue, `risiko.consequence.${catId}`);
                }}
              />
              <LocalErrorBoundary>
                <IssueRisikoMatrix issue={issue} issueBoard={issueBoard} evaluationCategoryCapital={catId} />
              </LocalErrorBoundary>
            </Stack>
          </McTitle>
        </Grid.Col>
      );
    });
  }, [issueBoard, issue.risiko]);

  return (
    <FoldableSection storageKey="issue-modal-risiko">
      <FoldableSection.Header>
        <Title order={6}>Risiko</Title>
      </FoldableSection.Header>
      <FoldableSection.Body>
        <Card w="100%" bg="#f8f9fa" bd="1px solid var(--mantine-color-warmgray-2)">
          <Grid flex={1}>
            <Grid.Col span={4}>
              <McTitle title="Sannsynlighet">
                <Select
                  allowDeselect={false}
                  value={current?.id}
                  data={(probabilities ?? []).filter((p) => p.isActive).map((p) => ({ label: p.name, value: p.id }))}
                  onChange={(newValue) => updateSimple({ risiko: { probability: newValue } })}
                />
              </McTitle>
            </Grid.Col>
            <Grid.Col span={8}></Grid.Col>
            {columns}
            <Grid.Col span={12}>
              <McTitle title="Kommentar">
                <McTextAreaNew
                  onBlur={(e) => updateSimple({ risiko: { categoryComment: e.target.value } })}
                  value={issue.risiko?.categoryComment}
                />
              </McTitle>
            </Grid.Col>
          </Grid>
        </Card>
      </FoldableSection.Body>
    </FoldableSection>
  );
};
